import { Link } from "@tanstack/react-router"
import { ChevronLeft, ChevronRight, RotateCw, ShieldAlert } from "lucide-react"
import { useState } from "react"
import { Trans } from "react-i18next"
import { JsonView } from "@/components/json-view"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import {
  CLASSIFICATION_COPY,
  describeOutcome,
  type Finding,
  type FindingSeverity,
  type FindingStatus,
  ruleLabel,
  ruleVersionLabel,
  SEVERITIES,
  STATUSES,
} from "@/lib/findings"
import { useT } from "@/lib/i18n"
import { type FindingFilters, useFindings } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"

const SEVERITY_TONE: Record<FindingSeverity, "danger" | "warning" | "neutral"> = {
  critical: "danger",
  error: "danger",
  warning: "warning",
  info: "neutral",
}

function relative(iso: string, t: ReturnType<typeof useT>): string {
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms)) return t("findings.lastSeenDate", { date: iso })
  const m = Math.round(ms / 60_000)
  if (m < 1) return t("findings.lastSeenNow")
  if (m < 60) return t("findings.lastSeenMinutes", { count: m })
  const h = Math.round(m / 60)
  if (h < 48) return t("findings.lastSeenHours", { count: h })
  return t("findings.lastSeenDays", { count: Math.round(h / 24) })
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T | ""
  options: readonly T[]
  onChange: (v: T | "") => void
}) {
  const t = useT()
  return (
    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | "")}
        className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-ring/40"
      >
        <option value="">{t("findings.any")}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )
}

function FindingRow({ finding }: { finding: Finding }) {
  const t = useT()
  const scope = useScope()
  const [open, setOpen] = useState(false)
  const copy = CLASSIFICATION_COPY[finding.classification]
  return (
    <li className="px-5 py-3.5">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={SEVERITY_TONE[finding.severity]}>{finding.severity}</Badge>
            <Badge tone={copy.tone} title={t(copy.hintKey)}>
              {t(copy.labelKey)}
            </Badge>
            <span className="text-sm text-foreground">{finding.message}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <code className="font-mono text-2xs" title={finding.ruleRef.version}>
              {ruleLabel(finding, t)} · {ruleVersionLabel(finding.ruleRef.version, t)}
            </code>
            <span title={t("findings.firstSeen", { date: finding.firstSeen })}>{relative(finding.lastSeen, t)}</span>
            {finding.status !== "open" ? <Badge>{finding.status}</Badge> : null}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {finding.subjects.map((s) => (
              <Link
                key={`${s.objectType}/${s.key}`}
                to="/$orgId/$environmentId/objects/$objectType/$key"
                params={{ ...scope, objectType: s.objectType, key: s.key }}
                className="inline-flex items-center gap-1 rounded-sm border border-border-subtle px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-border hover:text-primary"
              >
                {s.objectType} / {s.key} <ChevronRight className="rtl-mirror size-3" aria-hidden />
              </Link>
            ))}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? t("findings.hideEvidence") : t("findings.evidence")}
        </Button>
      </div>
      {open ? (
        <div className="mt-3 grid gap-3 rounded-md border border-border-subtle bg-card p-3 text-xs">
          <p className="text-foreground">{describeOutcome(finding.outcome, t)}</p>
          <p className="text-muted-foreground">{t(copy.hintKey)}</p>
          {finding.provenance.length > 0 ? (
            <ul className="space-y-1">
              {finding.provenance.map((p, i) => (
                <li key={i} className="text-muted-foreground">
                  <Trans
                    shouldUnescape
                    tOptions={{ interpolation: { escapeValue: true } }}
                    t={t}
                    i18nKey={p.observationIds?.length ? "findings.provenanceObservations" : "findings.provenance"}
                    values={{
                      source: `${p.provenance.source.system} · ${p.provenance.source.record}`,
                      mapping: p.provenance.mappingVersion,
                      recordedAt: p.provenance.recordedAt,
                      observations: p.observationIds?.join(", ") ?? "",
                    }}
                    components={{
                      sourceValue: <code className="font-mono text-foreground/85" />,
                      mapping: <code className="font-mono" />,
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-subtle-foreground">{t("findings.noLineage")}</p>
          )}
          <JsonView value={finding} className="max-h-64" />
        </div>
      ) : null}
    </li>
  )
}

export function FindingsPage() {
  const t = useT()
  const scope = useScope()
  const [status, setStatus] = useState<FindingStatus | "">("")
  const [severity, setSeverity] = useState<FindingSeverity | "">("")
  const [ruleId, setRuleId] = useState("")
  const [subjectType, setSubjectType] = useState("")
  const [subjectKey, setSubjectKey] = useState("")
  const [trail, setTrail] = useState<(string | null)[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const filters: FindingFilters = {
    status: status || undefined,
    severity: severity || undefined,
    ruleId: ruleId.trim() || undefined,
    subjectType: subjectType.trim() || undefined,
    subjectKey: subjectKey.trim() || undefined,
  }
  const page = useFindings(scope, filters, cursor)
  const filtered = Object.values(filters).some((v) => v !== undefined)
  // Filters are part of the cursor binding: any change starts again from the first page.
  const reset = () => {
    setTrail([])
    setCursor(null)
  }
  const more = Boolean(page.data?.nextCursor)
  const paged = more || trail.length > 0

  return (
    <>
      <PageHeader
        crumbs={[t("nav.findings")]}
        actions={
          <Button
            size="icon"
            variant="ghost"
            onClick={() => {
              void page.refetch()
            }}
            disabled={page.isFetching}
            aria-label={t("common.refresh")}
          >
            <RotateCw className={page.isFetching ? "animate-spin" : undefined} />
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto">
        <Screen width="board" className="px-4 py-6 md:px-6">
          <p className="mt-1 max-w-[75ch] text-sm text-muted-foreground">
            <Trans
              shouldUnescape
              tOptions={{ interpolation: { escapeValue: true } }}
              t={t}
              i18nKey="findings.description"
              components={{
                integrity: <span className="text-foreground" />,
                quality: <span className="text-foreground" />,
              }}
            />
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 border-y border-border-subtle py-3">
            <Select
              label={t("findings.filterStatus")}
              value={status}
              options={STATUSES}
              onChange={(v) => {
                setStatus(v)
                reset()
              }}
            />
            <Select
              label={t("findings.filterSeverity")}
              value={severity}
              options={SEVERITIES}
              onChange={(v) => {
                setSeverity(v)
                reset()
              }}
            />
            <Input
              value={ruleId}
              onChange={(e) => {
                setRuleId(e.target.value)
                reset()
              }}
              placeholder={t("findings.ruleId")}
              aria-label={t("findings.ruleId")}
              className="h-7 w-40 font-mono text-xs"
            />
            <Input
              value={subjectType}
              onChange={(e) => {
                setSubjectType(e.target.value)
                reset()
              }}
              placeholder={t("findings.objectType")}
              aria-label={t("findings.subjectObjectType")}
              className="h-7 w-48 font-mono text-xs"
            />
            <Input
              value={subjectKey}
              onChange={(e) => {
                setSubjectKey(e.target.value)
                reset()
              }}
              placeholder={t("findings.objectKey")}
              aria-label={t("findings.subjectKey")}
              className="h-7 w-32 font-mono text-xs"
            />
          </div>
          {page.isPending ? (
            <TableSkeleton rows={5} columns={3} />
          ) : page.isError ? (
            <ErrorState
              error={page.error}
              what={t("findings.dataQualityFindings")}
              onRetry={() => void page.refetch()}
              inline
            />
          ) : page.data.items.length === 0 ? (
            <EmptyState icon={<ShieldAlert />} title={filtered ? t("findings.noMatches") : t("findings.noneVisible")}>
              <p>{filtered ? t("findings.clearFilter") : t("findings.noFindingsDescription")}</p>
            </EmptyState>
          ) : (
            <Section bordered>
              <ul className={cn("divide-y divide-border", page.isFetching && "opacity-70")}>
                {page.data.items.map((f) => (
                  <FindingRow key={f.id} finding={f} />
                ))}
              </ul>
            </Section>
          )}
          {paged ? (
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="tabular">{t("common.page", { page: trail.length + 1 })}</span>
              <div className="ms-auto flex gap-1.5">
                <Button
                  size="sm"
                  disabled={trail.length === 0 || page.isFetching}
                  onClick={() => {
                    setCursor(trail[trail.length - 1] ?? null)
                    setTrail((t) => t.slice(0, -1))
                  }}
                >
                  <ChevronLeft className="rtl-mirror" /> {t("common.previous")}
                </Button>
                <Button
                  size="sm"
                  disabled={!more || page.isFetching}
                  onClick={() => {
                    const next = page.data?.nextCursor
                    if (!next) return
                    setTrail((t) => [...t, cursor])
                    setCursor(next)
                  }}
                >
                  {t("common.next")} <ChevronRight className="rtl-mirror" />
                </Button>
              </div>
            </div>
          ) : null}
        </Screen>
      </div>
    </>
  )
}
